/**
 * API client for Mobile Health Booking System
 */
import axios from 'axios';
import { decryptBookingId } from '../utils/crypto';

/**
 * Resolve the API base URL based on the current host.
 * - test-pplcdpap.theimagroup.com → test backend
 * - pplcdpap.theimagroup.com (prod) → prod backend
 * - anything else (local dev, etc.) → falls back to REACT_APP_API_URL or localhost
 *
 * This runtime check is intentional: CRA bakes .env.production into every
 * `npm run build`, so we can't differentiate test vs. prod at build time
 * without changing the deploy pipeline. Deciding by hostname keeps a single
 * build artifact safe to deploy to either environment.
 */
const resolveApiBaseUrl = () => {
    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
        const host = window.location.hostname.toLowerCase();
        if (host === 'test-pplcdpap.theimagroup.com') {
            return 'https://imaflexbookerv2-test.azurewebsites.net/api';
        }
        if (host === 'pplcdpap.theimagroup.com') {
            return 'https://imaflexbookerv2.azurewebsites.net/api';
        }
    }
    return process.env.REACT_APP_API_URL || 'http://localhost:7071/api';
};

const API_BASE_URL = resolveApiBaseUrl();

/**
 * Get available time slots for a location and date range
 */
export const getAvailableSlots = async (location, startDate, endDate) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/slots`, {
            params: {
                location,
                start_date: startDate,
                end_date: endDate
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching available slots:', error);
        throw error;
    }
};

/**
 * Get booking details by booking ID
 */
export const getBooking = async (bookingId) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/booking/${bookingId}`);
        return response.data;
    } catch (error) {
        if (error.response && error.response.status === 404) {
            return { success: false, booking: null };
        }
        console.error('Error fetching booking:', error);
        throw error;
    }
};

/**
 * Create a new booking
 */
export const createBooking = async (bookingData) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/booking`, bookingData);
        return response.data;
    } catch (error) {
        console.error('Error creating booking:', error);
        throw error;
    }
};

/**
 * Update an existing booking
 */
export const updateBooking = async (bookingId, bookingData) => {
    try {
        const response = await axios.put(`${API_BASE_URL}/booking/${bookingId}`, bookingData);
        return response.data;
    } catch (error) {
        console.error('Error updating booking:', error);
        throw error;
    }
};

/**
 * Cancel a booking
 */
export const cancelBooking = async (bookingId) => {
    try {
        const response = await axios.delete(`${API_BASE_URL}/booking/${bookingId}`);
        return response.data;
    } catch (error) {
        console.error('Error cancelling booking:', error);
        throw error;
    }
};

/**
 * Upload a document
 */
export const uploadDocument = async (personId, fileType, fileContents) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/upload_document`, {
            personId,
            fileType,
            fileContents
        });
        return response.data;
    } catch (error) {
        console.error('Error uploading document:', error);
        throw error;
    }
};
